import { localApi } from "./http";

export type DeploymentHistoryStatus =
    | "PACKAGE_CREATED"
    | "PACKAGE_FAILED"
    | "TRANSFERRING"
    | "TRANSFERRED"
    | "TRANSFER_FAILED";

export type DeploymentHistoryEntry = {
    id: string;
    siteId?: number | null;
    siteName?: string | null;
    vcsType: string;
    status: DeploymentHistoryStatus;
    createdAt: string;
    updatedAt: string;
    transferredAt?: string | null;
    since?: string | null;
    until?: string | null;
    sinceVersion?: string | null;
    untilVersion?: string | null;
    fileStatusType?: string | null;
    selectedVersions: string[];
    selectedFiles: string[];
    duplicateFileVersionMap?: Record<string, string>;
    targetOs?: "WINDOWS" | "MAC" | "LINUX" | null;
    packageFileName?: string | null;
    packagePath?: string | null;
    packageSize?: number | null;
    packageSha256?: string | null;
    packagingElapsedMs?: number | null;
    slowestPhase?: string | null;
    remoteHost?: string | null;
    remotePort?: number | null;
    remotePath?: string | null;
    transferElapsedMs?: number | null;
    failureMessage?: string | null;
};

export type DeploymentHistoryListResponse = {
    entries: DeploymentHistoryEntry[];
    total: number;
};

export type ExtractionSaveResponse = {
    fileName: string;
    path: string;
    size: number;
    directoryPath: string;
    historyId: string;
};

export type DeploymentTransferCredentials = {
    password?: string;
    privateKeyPassphrase?: string;
    sshHost?: string;
    sshPort?: number;
    sshUsername?: string;
    sshAuthType?: "PASSWORD" | "PRIVATE_KEY";
    sshPrivateKeyPath?: string;
    remoteUploadPath?: string;
    strictHostKeyChecking?: boolean;
    saveConnection?: boolean;
};

export type DeploymentCommandResult = {
    workingDirectory: string;
    stdout: string;
    stderr: string;
    exitCode: number;
    elapsedMs: number;
};

export type DeploymentTerminalSession = {
    sessionId: string;
    workingDirectory: string;
};

export type DeploymentTerminalOutput = {
    output: string;
    connected: boolean;
};

export const fetchDeploymentHistory = async (siteId?: number | null) => {
    const response = await localApi.get<DeploymentHistoryListResponse>("/api/deployments", {
        params: {
            siteId: siteId ?? undefined,
            limit: 200,
        },
    });
    return response.data;
};

export const transferDeployment = async (
    historyId: string,
    credentials: DeploymentTransferCredentials
) => {
    const response = await localApi.post<DeploymentHistoryEntry>(
        `/api/deployments/${encodeURIComponent(historyId)}/transfer`,
        credentials,
        { timeout: 1800000 }
    );
    return response.data;
};

export const repackageDeployment = async (historyId: string) => {
    const response = await localApi.post<ExtractionSaveResponse>(
        `/api/deployments/${encodeURIComponent(historyId)}/repackage`,
        undefined,
        { timeout: 1800000 }
    );
    return response.data;
};

export const executeDeploymentCommand = async (
    historyId: string,
    command: string,
    credentials: DeploymentTransferCredentials
) => {
    const response = await localApi.post<DeploymentCommandResult>(
        `/api/deployments/${encodeURIComponent(historyId)}/commands`,
        {
            command,
            password: credentials.password,
            privateKeyPassphrase: credentials.privateKeyPassphrase,
            sshHost: credentials.sshHost,
            sshPort: credentials.sshPort,
            sshUsername: credentials.sshUsername,
            sshAuthType: credentials.sshAuthType,
            sshPrivateKeyPath: credentials.sshPrivateKeyPath,
            strictHostKeyChecking: credentials.strictHostKeyChecking,
        },
        { timeout: 150000 }
    );
    return response.data;
};

const terminalCredentials = (credentials: DeploymentTransferCredentials) => ({
    password: credentials.password,
    privateKeyPassphrase: credentials.privateKeyPassphrase,
    sshHost: credentials.sshHost,
    sshPort: credentials.sshPort,
    sshUsername: credentials.sshUsername,
    sshAuthType: credentials.sshAuthType,
    sshPrivateKeyPath: credentials.sshPrivateKeyPath,
    strictHostKeyChecking: credentials.strictHostKeyChecking,
});

export const openDeploymentTerminal = async (
    historyId: string,
    credentials: DeploymentTransferCredentials
) => {
    const response = await localApi.post<DeploymentTerminalSession>(
        `/api/deployments/${encodeURIComponent(historyId)}/terminal`,
        terminalCredentials(credentials),
        { timeout: 30000 }
    );
    return response.data;
};

export const writeDeploymentTerminal = async (
    historyId: string,
    sessionId: string,
    input: string
) => {
    await localApi.post(
        `/api/deployments/${encodeURIComponent(historyId)}/terminal/${encodeURIComponent(sessionId)}/input`,
        { input },
        { timeout: 30000 }
    );
};

export const readDeploymentTerminal = async (
    historyId: string,
    sessionId: string
) => {
    const response = await localApi.get<DeploymentTerminalOutput>(
        `/api/deployments/${encodeURIComponent(historyId)}/terminal/${encodeURIComponent(sessionId)}/output`,
        { timeout: 30000 }
    );
    return response.data;
};

export const resizeDeploymentTerminal = async (
    historyId: string,
    sessionId: string,
    columns: number,
    rows: number
) => {
    await localApi.post(
        `/api/deployments/${encodeURIComponent(historyId)}/terminal/${encodeURIComponent(sessionId)}/resize`,
        { columns, rows },
        { timeout: 30000 }
    );
};

export const closeDeploymentTerminal = async (
    historyId: string,
    sessionId: string
) => {
    await localApi.delete(
        `/api/deployments/${encodeURIComponent(historyId)}/terminal/${encodeURIComponent(sessionId)}`,
        { timeout: 30000 }
    );
};

export const openSiteTerminal = async (
    siteId: number,
    credentials: DeploymentTransferCredentials
) => {
    const response = await localApi.post<DeploymentTerminalSession>(
        `/api/sites/${encodeURIComponent(siteId)}/terminal`,
        terminalCredentials(credentials),
        { timeout: 30000 }
    );
    return response.data;
};

export const writeSiteTerminal = async (
    siteId: number,
    sessionId: string,
    input: string
) => {
    await localApi.post(
        `/api/sites/${encodeURIComponent(siteId)}/terminal/${encodeURIComponent(sessionId)}/input`,
        { input },
        { timeout: 30000 }
    );
};

export const readSiteTerminal = async (
    siteId: number,
    sessionId: string
) => {
    const response = await localApi.get<DeploymentTerminalOutput>(
        `/api/sites/${encodeURIComponent(siteId)}/terminal/${encodeURIComponent(sessionId)}/output`,
        { timeout: 30000 }
    );
    return response.data;
};

export const resizeSiteTerminal = async (
    siteId: number,
    sessionId: string,
    columns: number,
    rows: number
) => {
    await localApi.post(
        `/api/sites/${encodeURIComponent(siteId)}/terminal/${encodeURIComponent(sessionId)}/resize`,
        { columns, rows },
        { timeout: 30000 }
    );
};

export const closeSiteTerminal = async (
    siteId: number,
    sessionId: string
) => {
    await localApi.delete(
        `/api/sites/${encodeURIComponent(siteId)}/terminal/${encodeURIComponent(sessionId)}`,
        { timeout: 30000 }
    );
};

export const openLocalTerminal = async (workingDirectory?: string) => {
    const response = await localApi.post<DeploymentTerminalSession>(
        "/api/local-terminal",
        { workingDirectory },
        { timeout: 30000 }
    );
    return response.data;
};

export const writeLocalTerminal = async (
    sessionId: string,
    input: string
) => {
    await localApi.post(
        `/api/local-terminal/${encodeURIComponent(sessionId)}/input`,
        { input },
        { timeout: 30000 }
    );
};

export const readLocalTerminal = async (sessionId: string) => {
    const response = await localApi.get<DeploymentTerminalOutput>(
        `/api/local-terminal/${encodeURIComponent(sessionId)}/output`,
        { timeout: 30000 }
    );
    return response.data;
};

export const resizeLocalTerminal = async (
    sessionId: string,
    columns: number,
    rows: number
) => {
    await localApi.post(
        `/api/local-terminal/${encodeURIComponent(sessionId)}/resize`,
        { columns, rows },
        { timeout: 30000 }
    );
};

export const closeLocalTerminal = async (sessionId: string) => {
    await localApi.delete(
        `/api/local-terminal/${encodeURIComponent(sessionId)}`,
        { timeout: 30000 }
    );
};

export const deleteDeploymentHistory = async (historyId: string) => {
    await localApi.delete(`/api/deployments/${encodeURIComponent(historyId)}`);
};
